import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Paintbrush, Phone, CheckCircle, Shield, Sparkles, Layers } from 'lucide-react';
import Lightbox from '../components/Lightbox';
import SEO from '../components/SEO';

const Peinture = () => {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const paintingTypes = [
    {
      icon: <Layers size={28} />,
      title: 'Peinture Complète',
      description: 'Remise en peinture intégrale de votre engin avec préparation de surface professionnelle.'
    },
    {
      icon: <Sparkles size={28} />,
      title: 'Retouches Partielles',
      description: 'Réparation localisée des zones endommagées : rayures, impacts, éclats.'
    },
    {
      icon: <Shield size={28} />,
      title: 'Traitement Anticorrosion',
      description: 'Protection durable contre la rouille et les agressions climatiques de La Réunion.'
    }
  ];

  const zones = [
    'Cabine opérateur',
    'Bras et flèche',
    'Châssis',
    'Godet',
    'Contre-poids',
    'Capots moteur',
    'Réservoirs',
    'Accessoires'
  ];

  const galleryImages = [
    { src: 'https://images.unsplash.com/photo-1763624578810-5ac518e4fd4c?auto=format&fit=crop&w=800&q=80', alt: 'Pelle hydraulique jaune', caption: 'Pelle hydraulique après peinture complète' },
    { src: 'https://images.unsplash.com/photo-1627451945663-5c1daa80cb20?auto=format&fit=crop&w=800&q=80', alt: 'Bulldozer Komatsu', caption: 'Bulldozer Komatsu - Finition jaune' },
    { src: 'https://images.pexels.com/photos/5657435/pexels-photo-5657435.png?auto=compress&cs=tinysrgb&w=800', alt: 'Peinture industrielle', caption: 'Application peinture en atelier' },
    { src: 'https://images.pexels.com/photos/5125783/pexels-photo-5125783.jpeg?auto=compress&cs=tinysrgb&w=800', alt: 'Tracto-pelle', caption: 'Tracto-pelle remis en peinture' },
    { src: 'https://images.pexels.com/photos/2058729/pexels-photo-2058729.jpeg?auto=compress&cs=tinysrgb&w=800', alt: 'Excavatrice', caption: 'Excavatrice après traitement' },
    { src: 'https://images.pexels.com/photos/5574291/pexels-photo-5574291.jpeg?auto=compress&cs=tinysrgb&w=800', alt: 'Pelle Komatsu', caption: 'Résultat final - Comme neuf' }
  ];

  const processSteps = [
    { step: '1', title: 'Nettoyage & Décapage', description: 'Préparation minutieuse de la surface' },
    { step: '2', title: 'Traitement Anticorrosion', description: 'Application de primaire protecteur' },
    { step: '3', title: 'Masquage', description: 'Protection des zones non peintes' },
    { step: '4', title: 'Peinture', description: 'Application en plusieurs couches' },
    { step: '5', title: 'Finitions', description: 'Marquages et vernis de protection' }
  ];

  const openLightbox = (index) => {
    setCurrentImageIndex(index);
    setLightboxOpen(true);
  };

  return (
    <div data-testid="peinture-page">
      <SEO
        title="Peinture Engins de Chantier La Réunion - Anticorrosion & Finition"
        description="Peinture professionnelle d'engins de chantier à La Réunion. Peinture complète, retouches, traitement anticorrosion adapté au climat réunionnais. Toutes marques : LiuGong, Komatsu, Caterpillar. Devis gratuit — Saint-Leu, toute l'île."
        canonical="/peinture"
        image="https://images.unsplash.com/photo-1652922660696-60c68ec51582?auto=format&fit=crop&w=1200&q=80"
      />
      {/* Hero Section */}
      <section className="hero-section hero-peinture min-h-[60vh]">
        <div className="absolute inset-0 bg-gradient-to-r from-black/80 to-black/40" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="max-w-2xl animate-slide-up">
            <span 
              className="text-sm font-bold tracking-widest uppercase text-[#F5C500] block mb-4"
              style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
            >
              Notre Service
            </span>
            <h1 
              className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6"
              style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
            >
              Peinture d'Engins de Chantier
            </h1>
            <p className="text-xl text-gray-300 mb-8 leading-relaxed">
              Redonnez une seconde jeunesse à vos engins. Peinture professionnelle, 
              finitions soignées et protection durable.
            </p>
            <Link to="/contact" className="btn-primary" data-testid="peinture-cta">
              Demander un Devis
            </Link>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="section-white py-20 md:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span 
              className="text-sm font-bold tracking-widest uppercase text-[#F5C500] block mb-4"
              style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
            >
              Types de Peinture
            </span>
            <h2 
              className="text-3xl md:text-4xl font-bold text-[#1a1a1a] mb-6"
              style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
            >
              Nos Prestations Peinture
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {paintingTypes.map((type, index) => (
              <div 
                key={index} 
                className="service-card bg-white shadow-lg p-8"
                data-testid={`painting-type-${index}`}
              >
                <div className="icon-container mb-6">
                  {type.icon}
                </div>
                <h3 
                  className="text-xl font-bold text-[#1a1a1a] mb-4"
                  style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
                >
                  {type.title}
                </h3>
                <p className="text-gray-600">{type.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Zones Section */}
      <section className="section-gray py-20 md:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <span 
                className="text-sm font-bold tracking-widest uppercase text-[#F5C500] block mb-4"
                style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
              >
                Zones de Peinture
              </span>
              <h2 
                className="text-3xl md:text-4xl font-bold text-[#1a1a1a] mb-8"
                style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
              >
                Nous Peignons Toutes les Parties de Votre Engin
              </h2>
              <div className="grid grid-cols-2 gap-4">
                {zones.map((zone, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <div className="w-3 h-3 bg-[#F5C500]" />
                    <span className="text-gray-700 font-medium">{zone}</span>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <img 
                src="https://images.unsplash.com/photo-1627451945663-5c1daa80cb20?auto=format&fit=crop&w=800&q=80"
                alt="Peinture engin de chantier"
                className="w-full shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="section-white py-20 md:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span 
              className="text-sm font-bold tracking-widest uppercase text-[#F5C500] block mb-4"
              style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
            >
              Notre Méthode
            </span>
            <h2 
              className="text-3xl md:text-4xl font-bold text-[#1a1a1a]"
              style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
            >
              Les Étapes de la Peinture
            </h2>
          </div>

          <div className="flex flex-wrap justify-center gap-4">
            {processSteps.map((step, index) => (
              <div 
                key={index} 
                className="bg-[#F4F4F5] p-6 flex-1 min-w-[200px] max-w-[250px]"
                data-testid={`paint-step-${index}`}
              >
                <div className="step-number mb-4">{step.step}</div>
                <h4 
                  className="font-bold text-[#1a1a1a] mb-2"
                  style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
                >
                  {step.title}
                </h4>
                <p className="text-gray-600 text-sm">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery Section */}
      <section className="section-gray py-20 md:py-32" data-testid="gallery-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span 
              className="text-sm font-bold tracking-widest uppercase text-[#F5C500] block mb-4"
              style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
            >
              Galerie
            </span>
            <h2 
              className="text-3xl md:text-4xl font-bold text-[#1a1a1a]"
              style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
            >
              Nos Réalisations Peinture
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {galleryImages.map((image, index) => (
              <div 
                key={index} 
                className="gallery-item aspect-[4/3]"
                onClick={() => openLightbox(index)}
                data-testid={`peinture-gallery-${index}`}
              >
                <img 
                  src={image.src} 
                  alt={image.alt}
                  className="w-full h-full object-cover"
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Brands Section */}
      <section className="section-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h3 
            className="text-2xl font-bold text-[#1a1a1a] mb-8"
            style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
          >
            Peinture pour Toutes les Marques
          </h3>
          <div className="flex flex-wrap justify-center gap-6 text-gray-500 font-semibold">
            {['LiuGong', 'Komatsu', 'Caterpillar', 'Kubota', 'Hitachi', 'Volvo', 'JCB', 'Bobcat'].map((brand, index) => (
              <span key={index} className="px-4 py-2 border border-gray-200 hover:border-[#F5C500] hover:text-[#1a1a1a] transition-colors">
                {brand}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-[#1a1a1a] py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 
            className="text-3xl md:text-4xl font-bold text-white mb-6"
            style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
          >
            Votre Engin Mérite une Nouvelle Peinture ?
          </h2>
          <p className="text-gray-400 text-lg mb-8 max-w-2xl mx-auto">
            Contactez-nous pour un devis personnalisé. Nous évaluons gratuitement l'état de votre engin.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact" className="btn-primary">
              Demander un Devis
            </Link>
            <a href="tel:0693526449" className="btn-secondary flex items-center justify-center gap-2">
              <Phone size={20} />
              06 93 52 64 49
            </a>
          </div>
        </div>
      </section>

      {/* Lightbox */}
      {lightboxOpen && (
        <Lightbox
          images={galleryImages}
          currentIndex={currentImageIndex}
          onClose={() => setLightboxOpen(false)}
          onPrev={() => setCurrentImageIndex(Math.max(0, currentImageIndex - 1))}
          onNext={() => setCurrentImageIndex(Math.min(galleryImages.length - 1, currentImageIndex + 1))}
        />
      )}
    </div>
  );
};

export default Peinture;
